import { useState, useEffect, useCallback } from 'react';

import { supabase } from '../../../lib/supabase';
import { QuizQuestion } from '../../../shared/types/quiz';
import { EnhancedQuestion } from '../../../types/questionFormats';
// Removed problematic import that was causing build failures

interface UseSessionLoaderProps {
  sessionId: string;
  appLanguage: string;
}

export function useSessionLoader({ sessionId, appLanguage }: UseSessionLoaderProps) {
  const [loadedQuestions, setLoadedQuestions] = useState<EnhancedQuestion[]>([]);
  const [roomLanguage, setRoomLanguage] = useState<string | null>(null);
  const [languageMismatch, setLanguageMismatch] = useState<boolean>(false);
  const [isLoadingSessionData, setIsLoadingSessionData] = useState<boolean>(true);
  const [sessionError, setSessionError] = useState<string | null>(null);
  const [sessionStatus, setSessionStatus] = useState<string | null>(null);

  const MAX_SESSION_FETCH_RETRIES = 5;

  const RETRY_DELAY_MS = 1200;

  useEffect(() => {
    let isMounted = true;
    let retryTimer: ReturnType<typeof setTimeout>;

    const wrappedLoadSession = async (retryCount = 0) => {
      if (!isMounted) return; // Prevent state updates on unmounted component

      if (!sessionId) {
        if (__DEV__) console.warn('[useSessionLoader] No sessionId provided, aborting session load.');
        setSessionError('No session ID provided. Please rejoin the game.');
        setIsLoadingSessionData(false);
        return;
      }
      setIsLoadingSessionData(true);
      setSessionError(null);

      const { data, error } = await supabase
        .from('multiplayer_game_sessions')
        .select('questions, language_code, status')
        .eq('id', sessionId)
        .single();

      if (error || !data) {
        if (retryCount < MAX_SESSION_FETCH_RETRIES) {
          retryTimer = setTimeout(() => wrappedLoadSession(retryCount + 1), RETRY_DELAY_MS);
          return;
        }
        setSessionError('Could not load session details');
        setIsLoadingSessionData(false);
        return;
      }

      if ('language_code' in data && data.language_code) {
        setRoomLanguage(data.language_code);
        setLanguageMismatch(data.language_code !== appLanguage);
      } else {
        setRoomLanguage(null);
        setLanguageMismatch(false);
      }
      setSessionStatus(data.status ?? null);

      let loadedQs: any[] = [];
      try {
        loadedQs = data.questions;
      } catch (e) {
        if (__DEV__) console.error('[useSessionLoader] Malformed questions:', data.questions, e);
        setSessionError('Malformed questions data');
        setIsLoadingSessionData(false);
        return;
      }

      if (!Array.isArray(loadedQs) || loadedQs.length === 0) {
        setSessionError('No questions found for this session.');
        setLoadedQuestions([]);
        setIsLoadingSessionData(false);
        return;
      }

      // Process questions to enhanced format with proper validation and randomization
      try {
        const processedQuestions = await Promise.all(
          loadedQs.map(async (q: any) => {
            try {
              console.log('🔥 [useSessionLoader] Raw question from database:', {
                questionId: q.id,
                hasFormatData: !!q.format_data,
                hasQuestionType: !!q.question_type,
                questionType: q.question_type,
                formatDataKeys: q.format_data ? Object.keys(q.format_data) : []
              });

              // If question is already in enhanced format, use it directly
              if (q.format_data && q.question_type) {
                // Extract options based on question type
                let options = [];
                let correctAnswer = '';
                
                if (q.question_type === 'mcq_single') {
                  const formatData = q.format_data;

                  console.log('🔥 [useSessionLoader] MCQ Single format_data:', {
                    questionId: q.id,
                    formatDataKeys: Object.keys(formatData || {}),
                    hasOptionsArray: !!formatData?.options,
                    optionsArray: formatData?.options,
                    hasIndividualOptions: !!(formatData?.option_a),
                    correct_answer: formatData?.correct_answer,
                    correct_option: formatData?.correct_option
                  });

                  // Handle two possible data structures:
                  // 1. New structure: format_data.options = ["opt1", "opt2", "opt3", "opt4"]
                  // 2. Old structure: format_data.option_a, option_b, option_c, option_d

                  if (Array.isArray(formatData?.options) && formatData.options.length > 0) {
                    // New structure: options is already an array
                    options = formatData.options;
                    correctAnswer = formatData?.correct_answer || '';
                    console.log('🔥 [useSessionLoader] Using options array structure');
                  } else if (formatData?.option_a) {
                    // Old structure: individual option fields
                    options = [
                      formatData.option_a,
                      formatData.option_b,
                      formatData.option_c,
                      formatData.option_d
                    ].filter(Boolean);

                    const correctLetter = formatData?.correct_option?.toLowerCase();
                    const optionMap: { [key: string]: string } = {
                      a: formatData.option_a,
                      b: formatData.option_b,
                      c: formatData.option_c,
                      d: formatData.option_d
                    };
                    correctAnswer = optionMap[correctLetter] || formatData?.correct_option || '';
                    console.log('🔥 [useSessionLoader] Using individual option fields structure');
                  } else {
                    console.warn('🔥 [useSessionLoader] No valid options structure found for MCQ Single question', q.id);
                    options = [];
                    correctAnswer = formatData?.correct_answer || '';
                  }

                  console.log('🔥 [useSessionLoader] MCQ Single final result:', {
                    questionId: q.id,
                    optionsCount: options.length,
                    options: options,
                    correctAnswer: correctAnswer
                  });
                } else if (q.question_type === 'true_false') {
                  options = ['True', 'False'];
                  correctAnswer = q.format_data.correct_answer ? 'True' : 'False';
                } else if (q.question_type === 'mcq_multiple') {
                  // MCQ Multiple options need IDs for proper selection tracking
                  // Add IDs if they don't exist
                  const optionsWithIds = q.format_data.options?.map((opt: any, index: number) => {
                    if (opt.id === undefined) {
                      return { ...opt, id: index + 1 };
                    }
                    return opt;
                  }) || [];
                  
                  options = optionsWithIds.map((opt: any) => opt.text || opt.value || opt);
                  const correctAnswers = optionsWithIds.filter((opt: any) => opt.correct === true)
                                                        .map((opt: any) => opt.text || opt.value || opt);
                  correctAnswer = correctAnswers.join(', ');
                  
                  // Update format_data with options that have IDs
                  q.format_data.options = optionsWithIds;
                } else {
                  // Other formats
                  options = q.format_data?.options || [];
                  correctAnswer = q.format_data?.correct_answer || q.answer || '';
                }
                
                const processedQuestion = {
                  id: q.id.toString(),
                  question_code: `Q_${q.id}`,
                  question: q.question,
                  question_type: q.question_type as any,
                  category_id: q.category_id,
                  difficulty_id: q.difficulty_id || 1,
                  active: true,
                  explanation: q.format_data?.explanation || q.explanation || '',
                  format_data: q.format_data,
                  options: options,
                  answer: correctAnswer,
                  correct_answer: correctAnswer
                };

                console.log('🔥 [useSessionLoader] Processed enhanced question:', {
                  id: processedQuestion.id,
                  question_type: processedQuestion.question_type,
                  hasOptions: !!processedQuestion.options,
                  optionsLength: processedQuestion.options?.length,
                  optionsActual: processedQuestion.options,
                  hasFormatData: !!processedQuestion.format_data,
                  formatDataKeys: processedQuestion.format_data ? Object.keys(processedQuestion.format_data) : []
                });

                return processedQuestion;
              }
              
              // Convert legacy format to enhanced format
              const legacyQuestion = {
                id: q.id,
                question: q.question,
                question_type: 'mcq_single' as const, // Default to MCQ Single for legacy questions
                category_id: q.category_id,
                difficulty_id: q.difficulty_id || 1,
                explanation: q.explanation,
                options: Array.isArray(q.options) ? q.options : [],
                answer: q.answer || '',
                created_at: q.created_at,
                updated_at: q.updated_at
              };
              
              // Process legacy question directly without external function
              return {
                id: legacyQuestion.id.toString(),
                question_code: `Q_${legacyQuestion.id}`,
                question: legacyQuestion.question,
                question_type: legacyQuestion.question_type as any,
                category_id: legacyQuestion.category_id,
                difficulty_id: legacyQuestion.difficulty_id,
                active: true,
                explanation: legacyQuestion.explanation,
                format_data: {
                  format: 'mcq_single',
                  options: legacyQuestion.options,
                  correct_answer: legacyQuestion.answer,
                  shuffle_options: true,
                  min_options: 2,
                  max_options: 6
                },
                options: legacyQuestion.options,
                answer: legacyQuestion.answer,
                correct_answer: legacyQuestion.answer
              };
            } catch (error) {
              console.error('[useSessionLoader] Error processing question:', q.id, error);
              // Fallback to basic question format
              return {
                id: q.id.toString(),
                question_code: `Q_${q.id}`,
                question: q.question,
                question_type: 'mcq_single' as const,
                category_id: q.category_id,
                difficulty_id: q.difficulty_id || 1,
                active: true,
                explanation: q.explanation,
                format_data: {
                  format: 'mcq_single',
                  options: Array.isArray(q.options) ? q.options : [],
                  correct_answer: q.answer || '',
                  shuffle_options: true,
                  min_options: 2,
                  max_options: 6
                },
                options: Array.isArray(q.options) ? q.options : [],
                answer: q.answer || '',
                correct_answer: q.answer || ''
              };
            }
          })
        );
        
        setLoadedQuestions(processedQuestions as unknown as EnhancedQuestion[]);
      } catch (error) {
        console.error('[useSessionLoader] Error processing questions:', error);
        setSessionError('Error processing questions data');
        setIsLoadingSessionData(false);
        return;
      }
      setIsLoadingSessionData(false);
    };

    wrappedLoadSession(0);

    return () => {
      isMounted = false;
      clearTimeout(retryTimer);
    };
  }, [sessionId, appLanguage]);

  const reloadSession = useCallback(() => {
    setIsLoadingSessionData(true);
    setSessionError(null);
    // Trigger re-run of the effect by clearing and reloading
    setLoadedQuestions([]);
  }, []);

  return {
    loadedQuestions,
    roomLanguage,
    languageMismatch,
    isLoadingSessionData,
    sessionError,
    sessionStatus,
    reloadSession,
  };
}
